/**
 * Bottom Navigation Component
 * 5 navigation items: Store, Cart, Dashboard, Wallet, Profile
 */

import React from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { useTranslation } from '../../hooks/useTranslation';

interface NavItemConfig {
  id: string;
  labelKey: string;
  icon: string;
  path: string;
}

const BottomNavigation: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { botUsername } = useParams<{ botUsername: string }>();
  const { t } = useTranslation();

  const navItems: NavItemConfig[] = [
    { id: 'store', labelKey: 'shop.store', icon: 'store', path: `/shop/${botUsername}` },
    { id: 'cart', labelKey: 'cart.title', icon: 'cart', path: `/shop/${botUsername}/cart` },
    { id: 'dashboard', labelKey: 'dashboard.title', icon: 'dashboard', path: `/shop/${botUsername}/dashboard` },
    { id: 'wallet', labelKey: 'wallet.wallet', icon: 'wallet', path: `/shop/${botUsername}/wallet` },
    { id: 'profile', labelKey: 'profile.profile', icon: 'profile', path: `/shop/${botUsername}/profile` },
  ];

  const isActive = (path: string) => {
    if (path === `/shop/${botUsername}`) {
      return location.pathname === path || location.pathname.startsWith(`/shop/${botUsername}/product`);
    }
    return location.pathname.startsWith(path);
  };

  const handleNavClick = (item: NavItemConfig) => {
    navigate(item.path);
    // Haptic feedback
    window.Telegram?.WebApp?.HapticFeedback?.impactOccurred('light');
  };

  const getIcon = (iconName: string, active: boolean) => {
    const color = active ? 'var(--tg-theme-button-color, #3390ec)' : 'var(--tg-theme-hint-color, #999)';

    switch (iconName) {
      case 'store':
        return (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2">
            <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
            <polyline points="9,22 9,12 15,12 15,22" />
          </svg>
        );
      case 'cart':
        return (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2">
            <circle cx="9" cy="21" r="1" />
            <circle cx="20" cy="21" r="1" />
            <path d="M1 1h4l2.68 13.39a2 2 0 002 1.61h9.72a2 2 0 002-1.61L23 6H6" />
          </svg>
        );
      case 'dashboard':
        return (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2">
            <rect x="3" y="3" width="7" height="9" rx="1" />
            <rect x="14" y="3" width="7" height="5" rx="1" />
            <rect x="14" y="12" width="7" height="9" rx="1" />
            <rect x="3" y="16" width="7" height="5" rx="1" />
          </svg>
        );
      case 'wallet':
        return (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2">
            <path d="M21 4H3a2 2 0 00-2 2v12a2 2 0 002 2h18a2 2 0 002-2V6a2 2 0 00-2-2z" />
            <path d="M16 12h.01" />
            <path d="M1 10h22" />
          </svg>
        );
      case 'profile':
        return (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2">
            <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" />
            <circle cx="12" cy="7" r="4" />
          </svg>
        );
      default:
        return null;
    }
  };

  return (
    <NavContainer>
      <NavWrapper>
        {navItems.map((item) => {
          const active = isActive(item.path);
          return (
            <NavItem
              key={item.id}
              onClick={() => handleNavClick(item)}
              $active={active}
            >
              <IconWrapper>{getIcon(item.icon, active)}</IconWrapper>
              <NavLabel $active={active}>{t(item.labelKey)}</NavLabel>
            </NavItem>
          );
        })}
      </NavWrapper>
    </NavContainer>
  );
};

const NavContainer = styled.nav`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: var(--tg-theme-bg-color, #ffffff);
  border-top: 1px solid var(--tg-theme-hint-color, #e0e0e0);
  padding-bottom: env(safe-area-inset-bottom, 0);
  z-index: 1000;
`;

const NavWrapper = styled.div`
  display: flex;
  justify-content: space-around;
  align-items: center;
  height: 56px;
  max-width: 500px;
  margin: 0 auto;
`;

const NavItem = styled.button<{ $active: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  height: 100%;
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px 0;
  transition: opacity 0.2s;

  &:active {
    opacity: 0.7;
  }
`;

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 24px;
  margin-bottom: 2px;
`;

const NavLabel = styled.span<{ $active: boolean }>`
  font-size: 10px;
  font-weight: ${props => props.$active ? '600' : '400'};
  color: ${props => props.$active
    ? 'var(--tg-theme-button-color, #3390ec)'
    : 'var(--tg-theme-hint-color, #999)'};
`;

export default BottomNavigation;
